import { ref, computed } from 'vue';
import type { FormValidation } from '../types/models';
import { isRequired, isValidEmail, isValidPassword } from '../utils/validators';

interface ValidationRule {
    validator: (value: unknown) => boolean;
    message: string;
}

interface FieldValidation {
    value: unknown;
    rules: ValidationRule[];
    errors: string[];
}

export function useForm<T extends Record<string, unknown>>(initialValues: T) {
    const values = ref<T>({ ...initialValues });
    const touched = ref<Record<string, boolean>>({});
    const validations = ref<Record<string, FieldValidation>>({});
    const isSubmitting = ref(false);

    const setFieldValue = (field: keyof T, value: unknown) => {
        values.value[field as string] = value;
        validateField(field as string);
    };

    const setFieldTouched = (field: keyof T, isTouched = true) => {
        touched.value[field as string] = isTouched;
        if (isTouched) {
            validateField(field as string);
        }
    };

    const resetForm = () => {
        values.value = { ...initialValues };
        touched.value = {};
        Object.keys(validations.value).forEach(field => {
            validations.value[field].errors = [];
        });
    };

    const addValidation = (field: keyof T, rules: ValidationRule[]) => {
        validations.value[field as string] = {
            value: values.value[field as string],
            rules,
            errors: []
        };
    };

    const validateField = (field: string): boolean => {
        const fieldValidation = validations.value[field];
        if (!fieldValidation) return true;

        const fieldValue = values.value[field];
        fieldValidation.errors = [];

        fieldValidation.rules.forEach(rule => {
            if (!rule.validator(fieldValue)) {
                fieldValidation.errors.push(rule.message);
            }
        });

        return fieldValidation.errors.length === 0;
    };

    const validateForm = (): FormValidation => {
        const errors: Record<string, string[]> = {};
        let isValid = true;

        Object.keys(validations.value).forEach(field => {
            const fieldIsValid = validateField(field);
            if (!fieldIsValid) {
                errors[field] = validations.value[field].errors;
                isValid = false;
            }
        });

        return { isValid, errors };
    };

    // Common validation rules
    const required = (message = 'This field is required'): ValidationRule => ({
        validator: isRequired,
        message
    });

    const email = (message = 'Invalid email address'): ValidationRule => ({
        validator: (value) => typeof value === 'string' && isValidEmail(value),
        message
    });

    const password = (message = 'Password does not meet requirements'): ValidationRule => ({
        validator: (value) => typeof value === 'string' && isValidPassword(value).isValid,
        message
    });

    const minLength = (length: number, message = `Minimum length is ${length}`): ValidationRule => ({
        validator: (value) => typeof value === 'string' && value.length >= length,
        message
    });

    const maxLength = (length: number, message = `Maximum length is ${length}`): ValidationRule => ({
        validator: (value) => typeof value === 'string' && value.length <= length,
        message
    });

    return {
        values: computed(() => values.value),
        touched: computed(() => touched.value),
        errors: computed(() => {
            const errors: Record<string, string[]> = {};
            Object.keys(validations.value).forEach(field => {
                if (validations.value[field].errors.length > 0) {
                    errors[field] = validations.value[field].errors;
                }
            });
            return errors;
        }),
        isSubmitting: computed(() => isSubmitting.value),
        setFieldValue,
        setFieldTouched,
        resetForm,
        addValidation,
        validateForm,
        // Validation rules
        rules: {
            required,
            email,
            password,
            minLength,
            maxLength
        }
    };
}
